import { buildSectionSelector, normalizeSectionId, morphSection, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentLoaded } from '@theme/utilities';

/**
 * True while the mobile menu is open or playing its close animation.
 * Hydrating during that window steals the main thread and can eat the first tap.
 */
function isMobileDrawerBusy() {
  const drawerDetails = document.querySelector('header-drawer .header-drawer__details');
  return Boolean(drawerDetails?.open || drawerDetails?.classList.contains('is-closing'));
}

/**
 * @param {string} sectionId
 * @param {URL} [url]
 */
function hydrateWhenDrawerIdle(sectionId, url) {
  const drawerDetails = document.querySelector('header-drawer .header-drawer__details');
  drawerDetails?.addEventListener('toggle', () => hydrateSection(sectionId, url), { once: true });
}

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  if (isMobileDrawerBusy()) {
    hydrateWhenDrawerIdle(sectionId, url);
    return;
  }

  const html = await sectionRenderer.getSectionHTML(normalizedId, false, url);

  if (isMobileDrawerBusy()) {
    hydrateWhenDrawerIdle(sectionId, url);
    return;
  }

  // The desktop mega menu is hidden on phones and is expensive to morph.
  if (window.matchMedia('(max-width: 749px)').matches) {
    section.querySelector('header-menu[data-hydration-key="header-menu"]')?.removeAttribute('data-hydration-key');
  }

  morphSection(normalizedId, html, { mode: 'hydration' });

  section.dataset.hydrated = 'true';
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the page has finished loading so the first menu tap is not competing for the main thread.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentLoaded(() => {
    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}
